import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";

import type {
  SpecLineCreateBrowserInput,
  SpecLineDeleteBrowserInput,
  SpecLineDuplicateBrowserInput,
  SpecLineMoveBrowserInput,
  SpecLineStatusBrowserInput,
  SpecLineUpdateBrowserInput,
} from "@/api/contracts/spec-editor";
import {
  specLineCreate,
  specLineDelete,
  specLineDuplicate,
  specLineMove,
  specLineUpdate,
  specLineUpdateStatus,
} from "@/api/functions/spec-editor";
import {
  editorMutationOptions,
  NO_ROWS,
  patchLines,
  type EditorGuess,
} from "@/hooks/spec-editor-cache";

// The Editor's line verbs (#800): what #807's dialogs, #809's rows (the pill,
// the lock, the drag) and #810's line menu call. Each runs through #797's
// harness (`editorMutationOptions`) with its own `ERRORS` key, and its result
// lands through the one cache updater — the lines, groups, orderings, cursors,
// summary and gating the server computed in the write's transaction (ADR-0014
// decisions 1–6).
//
// **Only the status and the move guess** (decision 5 as amended; the
// structure rulings item 8): the pill and the drag show their change at once,
// and the harness rolls it back on failure. Add specification line, Edit
// specification line, Delete and Duplicate wait for the server — their dialogs
// and confirm hold until the result lands, and a failure leaves every row as it
// was.
//
// Each hook's variables are the verb's browser input, which is also its cache
// target (`specificationId`, `versionId`, `scope`). No hook touches the Active
// selection: the dialogs and rows set and clear it (#807, #809, #810), and Add
// specification line's resolves to the new line's id for that.

/** Add specification line's answer: the shared result and the new line's id —
 *  the server function's own type, so it cannot drift from the wire face. */
export type SpecLineCreateResult = Awaited<ReturnType<typeof specLineCreate>>;

/** Add specification line — appended to its group, with `Selection 1`. */
export function createSpecLineMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecLineCreateBrowserInput, SpecLineCreateResult>(queryClient, {
    mutationFn: (data) => specLineCreate({ data }),
    error: "ADD_LINE",
    structural: true,
  });
}

export function useCreateSpecLine() {
  return useMutation(createSpecLineMutationOptions(useQueryClient()));
}

/** Edit specification line — name and note together, and a group move. */
export function updateSpecLineMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecLineUpdateBrowserInput>(queryClient, {
    mutationFn: (data) => specLineUpdate({ data }),
    error: "UPDATE_LINE_DETAILS",
    // A group move only (`lines.py:404-435`): name and notes keep the orderings.
    structural: ({ lineId, groupId }, _result, held) => held(lineId)?.groupId !== groupId,
  });
}

export function useUpdateSpecLine() {
  return useMutation(updateSpecLineMutationOptions(useQueryClient()));
}

/**
 * The pill's guess: the line's status, in every cached view that holds it —
 * the source's `{lines: [{id, status}]}` (`LineStatus/actions.ts:77-83`). The
 * group's status and the version's gating wait for the result, which lands in
 * the same round trip. Idempotent.
 */
export const updateSpecLineStatusGuess: EditorGuess<SpecLineStatusBrowserInput> = (
  data,
  { lineId, status },
) => {
  const line = data.pages.flatMap((page) => page?.lines ?? []).find(({ id }) => id === lineId);
  if (!line) return data;
  return patchLines(data, { ...NO_ROWS, lines: [{ ...line, status }] }, null, false);
};

/** Line status — the pill, and the lock's `in_progress`; optimistic. */
export function updateSpecLineStatusMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecLineStatusBrowserInput>(queryClient, {
    mutationFn: (data) => specLineUpdateStatus({ data }),
    error: "UPDATE_LINE_STATUS",
    guess: updateSpecLineStatusGuess,
  });
}

export function useUpdateSpecLineStatus() {
  return useMutation(updateSpecLineStatusMutationOptions(useQueryClient()));
}

/** Delete line — the line and its selections; usage recounted. */
export function deleteSpecLineMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecLineDeleteBrowserInput>(queryClient, {
    mutationFn: (data) => specLineDelete({ data }),
    error: "DELETE_LINE",
    // Only a line that leaves the orderings (`lines.py:192-215`): a struck
    // line comes back whole and stays where it was.
    structural: ({ lineId }, { removedLineIds }) => removedLineIds.includes(lineId),
  });
}

export function useDeleteSpecLine() {
  return useMutation(deleteSpecLineMutationOptions(useQueryClient()));
}

/**
 * The drag's guess: the group's held lines reordered by the rule the server
 * applies — the moved line immediately after `afterId`, first for `null` —
 * laid on through the updater's own `patchLines` as that group's ordering, so
 * the page cursor, total and `hasMore` stay as they were. A page set that does
 * not hold the line, or the line it lands after, is left as it is. Idempotent:
 * laid over pages that already show the move, it changes nothing.
 */
export const moveSpecLineGuess: EditorGuess<SpecLineMoveBrowserInput> = (
  data,
  { lineId, afterId },
) => {
  const last = data.pages[data.pages.length - 1];
  if (!last) return data;
  const held = data.pages.flatMap((page) => page?.lines ?? []);
  const moved = held.find(({ id }) => id === lineId);
  if (!moved) return data;
  const order = held.filter((line) => line.groupId === moved.groupId).map(({ id }) => id);
  if (afterId === lineId || (afterId !== null && !order.includes(afterId))) return data;
  const rest = order.filter((id) => id !== lineId);
  rest.splice(afterId === null ? 0 : rest.indexOf(afterId) + 1, 0, lineId);
  // Every group of the version travels on every page (`get_version_lines_page`),
  // so the last page's groups are the set the updater itself reads.
  const group = last.groups.find(({ id }) => id === moved.groupId);
  if (!group) return data;
  return patchLines(
    data,
    NO_ROWS,
    {
      kind: "base",
      term: null,
      cursor: last.cursor,
      total: last.total,
      hasMore: last.hasMore,
      groupOrder: null,
      groups: [
        {
          groupId: moved.groupId,
          lineCount: group.lineCount,
          orderedLineIds: rest,
          loadedLineCount: rest.length,
        },
      ],
    },
    false,
  );
};

/** Move line — after `afterId` in its own group, first for `null`; optimistic. */
export function moveSpecLineMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecLineMoveBrowserInput>(queryClient, {
    mutationFn: (data) => specLineMove({ data }),
    error: "MOVE_LINE",
    guess: moveSpecLineGuess,
    structural: true,
  });
}

export function useMoveSpecLine() {
  return useMutation(moveSpecLineMutationOptions(useQueryClient()));
}

/** Duplicate line — the copy where the source's cloner puts it, under the name sent. */
export function duplicateSpecLineMutationOptions(queryClient: QueryClient) {
  return editorMutationOptions<SpecLineDuplicateBrowserInput>(queryClient, {
    mutationFn: (data) => specLineDuplicate({ data }),
    error: "DUPLICATE_LINE",
    structural: true,
  });
}

export function useDuplicateSpecLine() {
  return useMutation(duplicateSpecLineMutationOptions(useQueryClient()));
}
